import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  CheckCircle2,
  MapPin,
  PawPrint,
  Calendar,
  Phone,
  Mail,
  Building2,
  ShieldCheck,
  FileBadge2,
  ChevronRight,
  Share2,
  Heart,
  Download,
  Users,
  IndianRupee,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { Badge, CauseCard, InstitutionCard } from "@/components/cards";
import { causes, getInstitution, formatINR, stories, institutions, IMG } from "@/data/sample";

export const Route = createFileRoute("/institutions/$id")({
  loader: ({ params }) => {
    const inst = getInstitution(params.id);
    if (!inst) throw notFound();
    return inst;
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: `${loaderData?.name ?? "Institution"} — Animal Seva` },
      { name: "description", content: loaderData?.about ?? "" },
    ],
  }),
  component: InstitutionDetail,
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <h1 className="font-display text-2xl">Institution not found</h1>
        <Link to="/institutions" className="mt-4 inline-block text-[var(--maroon)] underline">
          Back to listings
        </Link>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ reset }) => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <p>Something went wrong loading this institution.</p>
        <button onClick={reset} className="mt-4 text-[var(--maroon)] underline">
          Try again
        </button>
      </div>
    </SiteShell>
  ),
});

function InstitutionDetail() {
  const inst = Route.useLoaderData();
  const instCauses = causes.filter((c) => c.institutionId === inst.id);
  const instStories = stories.filter((s) => s.institutionId === inst.id);
  const similar = institutions.filter((i) => i.id !== inst.id && i.type === inst.type).slice(0, 3);
  const gallery = [
    inst.cover,
    IMG.heroCow,
    IMG.heroDog,
    IMG.heroBird,
    IMG.causeFodder,
    IMG.causeWater,
  ].slice(0, 6);
  const usage = [
    { label: "Fodder & Feed", pct: 42, color: "var(--maroon)" },
    { label: "Medical & Surgery", pct: 28, color: "var(--saffron-deep)" },
    { label: "Shelter & Infrastructure", pct: 18, color: "#4a7a6a" },
    { label: "Rescue Operations", pct: 12, color: "#a35b3b" },
  ];
  const faqs = [
    {
      q: "How will my donation be used?",
      a: `100% of your donation goes to ${inst.name}. Funds are released against approved causes, and the institution must post photo + expense proof for every milestone.`,
    },
    {
      q: "Will I get a tax receipt?",
      a: inst.has80G
        ? "Yes. This institution holds valid 80G certification. You will receive an 80G receipt by email within minutes of a successful donation."
        : "This institution does not currently hold 80G certification, so donations are not eligible for tax deduction. A regular donation receipt will be issued.",
    },
    {
      q: "Can I visit the institution?",
      a: `Yes — visitors are welcome. Please call ${inst.contactPerson} on ${inst.phone} to schedule a visit.`,
    },
    {
      q: "Can I sponsor a single animal?",
      a: "Yes. Pick a monthly fodder / care cause and choose a recurring amount on the donation page.",
    },
  ];

  return (
    <SiteShell>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-foreground/60 sm:px-6">
          <Link to="/" className="hover:text-[var(--maroon)]">
            Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/institutions" className="hover:text-[var(--maroon)]">
            Institutions
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground/80">{inst.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative">
        <div className="h-72 sm:h-96 w-full overflow-hidden">
          <img src={inst.cover} alt={inst.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--maroon-deep)]/85 via-[var(--maroon-deep)]/30 to-transparent" />
        </div>
        <div className="mx-auto -mt-24 max-w-7xl px-4 sm:px-6 relative">
          <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-[var(--shadow-card)] sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {inst.verified && (
                    <Badge tone="verified">
                      <CheckCircle2 className="h-3 w-3" />
                      Admin Verified
                    </Badge>
                  )}
                  {inst.has80G && <Badge tone="featured">80G Tax Benefit</Badge>}
                  <Badge tone="neutral">{inst.type}</Badge>
                  <Badge tone="neutral">Est. {inst.established}</Badge>
                </div>
                <h1 className="mt-3 font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">
                  {inst.name}
                </h1>
                <p className="mt-2 flex items-center gap-1.5 text-sm text-foreground/65">
                  <MapPin className="h-4 w-4" /> {inst.address}, {inst.city}, {inst.state} –{" "}
                  {inst.pincode}
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-4">
                  <MiniStat
                    icon={PawPrint}
                    value={inst.animalCount.toLocaleString("en-IN")}
                    label="Animals"
                  />
                  <MiniStat
                    icon={Building2}
                    value={inst.shelterCapacity.toLocaleString("en-IN")}
                    label="Capacity"
                  />
                  <MiniStat
                    icon={Users}
                    value={instCauses.reduce((a, c) => a + c.donors, 0).toString()}
                    label="Donors"
                  />
                  <MiniStat
                    icon={IndianRupee}
                    value={formatINR(inst.totalDonations)}
                    label="Raised"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:min-w-[220px]">
                <Link
                  to="/donate"
                  search={{ institution: inst.id }}
                  className="rounded-full bg-[var(--maroon)] px-6 py-3 text-center text-sm font-semibold text-[var(--cream)] shadow-[var(--shadow-soft)] hover:bg-[var(--maroon-deep)]"
                >
                  Donate to this institution
                </Link>
                <div className="flex gap-2">
                  <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-white px-3 py-2 text-xs font-medium text-foreground/75 hover:bg-[var(--cream-2)]">
                    <Heart className="h-3.5 w-3.5" /> Save
                  </button>
                  <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-white px-3 py-2 text-xs font-medium text-foreground/75 hover:bg-[var(--cream-2)]">
                    <Share2 className="h-3.5 w-3.5" /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-12">
          <Block title="About the institution" eyebrow="Our story">
            <p className="text-foreground/80 leading-relaxed">{inst.about}</p>
            <div className="mt-5 rounded-2xl border-l-4 border-[var(--saffron)] bg-[var(--cream-2)] p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--saffron-deep)]">
                Mission
              </p>
              <p className="mt-1 text-foreground/85 italic">"{inst.mission}"</p>
            </div>
          </Block>

          <Block title="Photo gallery" eyebrow="Inside the campus">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {gallery.map((src, i) => (
                <div
                  key={i}
                  className={`overflow-hidden rounded-xl shadow-[var(--shadow-soft)] ${i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[4/3]"}`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </Block>

          <Block
            title="Active causes"
            eyebrow="Help right now"
            trailing={
              <Link to="/causes" className="text-sm text-[var(--maroon)] hover:underline">
                Explore all causes →
              </Link>
            }
          >
            {instCauses.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-[var(--cream-2)] p-8 text-center text-sm text-foreground/60">
                No active causes right now. Check back soon.
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {instCauses.map((c) => (
                  <CauseCard key={c.id} cause={c} />
                ))}
              </div>
            )}
          </Block>

          <Block title="Where your money goes" eyebrow="Transparency">
            <p className="text-sm text-foreground/70">
              Aggregated allocation across all causes hosted by {inst.name} over the last 12 months.
            </p>
            <div className="mt-5 space-y-4">
              {usage.map((u) => (
                <div key={u.label}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-foreground/80">{u.label}</span>
                    <span className="text-foreground/60">{u.pct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${u.pct}%`, background: u.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Block>

          {instStories.length > 0 && (
            <Block title="Impact stories" eyebrow="Real lives changed">
              <div className="grid gap-4 sm:grid-cols-2">
                {instStories.map((s) => (
                  <div
                    key={s.id}
                    className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)]"
                  >
                    <img
                      src={s.image}
                      alt=""
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover"
                    />
                    <div className="p-5">
                      <p className="text-xs text-foreground/70">
                        {new Date(s.date).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                      <h3 className="mt-1 font-display text-lg font-semibold leading-snug">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground/70 line-clamp-2">{s.excerpt}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Block>
          )}

          <Block title="Recent impact updates" eyebrow="From the field">
            <ol className="relative space-y-6 border-l-2 border-[var(--cream-2)] pl-6">
              {[
                {
                  date: "20 Jun 2026",
                  title: "Monthly fodder consignment delivered",
                  body: "12 tonnes of green fodder secured for 340 cattle. Bills uploaded to donor receipts.",
                },
                {
                  date: "11 Jun 2026",
                  title: "Calf 'Gauri' completed leg surgery",
                  body: "Surgery successful. Photos shared with all 142 cause donors via email.",
                },
                {
                  date: "02 Jun 2026",
                  title: "New water trough installed in west pasture",
                  body: "Funded by infrastructure cause donors. Capacity: 4,000L.",
                },
              ].map((u, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--maroon)] text-white">
                    <Sparkles className="h-2.5 w-2.5" />
                  </span>
                  <p className="text-xs text-foreground/70">{u.date}</p>
                  <p className="mt-0.5 font-semibold text-foreground">{u.title}</p>
                  <p className="mt-1 text-sm text-foreground/70">{u.body}</p>
                </li>
              ))}
            </ol>
          </Block>

          <Block title="Frequently asked questions" eyebrow="Donor FAQs">
            <div className="divide-y divide-border/60 rounded-2xl border border-border/60 bg-card">
              {faqs.map((f) => (
                <details key={f.q} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-foreground">
                    {f.q}
                    <ChevronRight className="h-4 w-4 transition group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm text-foreground/70">{f.a}</p>
                </details>
              ))}
            </div>
          </Block>
        </div>

        {/* Sticky sidebar */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl bg-[var(--maroon)] p-6 text-[var(--cream)] shadow-[var(--shadow-card)]">
            <p className="text-xs uppercase tracking-wider text-[var(--saffron)]">Total raised</p>
            <p className="mt-1 font-display text-3xl font-semibold">
              {formatINR(inst.totalDonations)}
            </p>
            <p className="mt-1 text-xs text-[var(--cream)]/70">
              across {instCauses.length} active cause{instCauses.length !== 1 ? "s" : ""}
            </p>
            <Link
              to="/donate"
              search={{ institution: inst.id }}
              className="mt-5 block rounded-full bg-white px-6 py-3 text-center text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)]"
            >
              Donate Now
            </Link>
            <p className="mt-3 text-center text-[11px] text-[var(--cream)]/70 flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3" /> 100% goes to this institution
            </p>
          </div>

          <SideCard title="Trust & verification">
            <ul className="space-y-2 text-sm">
              <Check label="Legal documents verified" ok={inst.verified} />
              <Check label="Bank account verified" ok={inst.verified} />
              <Check label="Contact & address verified" ok={inst.verified} />
              <Check label="80G certificate" ok={inst.has80G} />
              <Check label="12A certificate" ok={inst.has80G} />
            </ul>
          </SideCard>

          <SideCard title="At a glance">
            <div className="space-y-3">
              <Fact icon={Calendar} label="Established" value={String(inst.established)} />
              <Fact icon={ShieldCheck} label="Registration" value={inst.registrationNo} />
              <Fact icon={PawPrint} label="Animals served" value={inst.animals.join(", ")} />
              <Fact icon={TrendingUp} label="Active causes" value={String(instCauses.length)} />
            </div>
          </SideCard>

          <SideCard title="Contact">
            <ul className="space-y-2.5 text-sm text-foreground/80">
              <li className="flex items-center gap-2">
                <FileBadge2 className="h-4 w-4 text-[var(--maroon)]" />
                {inst.contactPerson}
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[var(--maroon)]" />
                {inst.phone}
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[var(--maroon)]" />
                {inst.email}
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--maroon)]" />
                <span>
                  {inst.address}, {inst.city} – {inst.pincode}
                </span>
              </li>
            </ul>
          </SideCard>

          <SideCard title="Documents">
            <ul className="space-y-2 text-sm">
              {[
                "Registration Certificate",
                inst.has80G ? "80G Certificate" : null,
                inst.has80G ? "12A Certificate" : null,
                "PAN Card",
                "Annual Report 2025",
              ]
                .filter(Boolean)
                .map((d) => (
                  <li
                    key={d as string}
                    className="flex items-center justify-between rounded-lg bg-[var(--cream-2)] px-3 py-2"
                  >
                    <span className="text-foreground/75">{d}</span>
                    <button className="text-[var(--maroon)] hover:text-[var(--maroon-deep)]">
                      <Download className="h-4 w-4" />
                    </button>
                  </li>
                ))}
            </ul>
          </SideCard>
        </aside>
      </section>

      {/* Similar institutions */}
      {similar.length > 0 && (
        <section className="border-t border-border/60 bg-[var(--cream-2)] py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
                  Similar institutions
                </p>
                <h2 className="mt-2 font-display text-2xl font-semibold text-[var(--maroon-deep)] sm:text-3xl">
                  Other verified {inst.type}s
                </h2>
              </div>
              <Link
                to="/institutions"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[var(--maroon)] hover:underline"
              >
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {similar.map((i) => (
                <InstitutionCard key={i.id} inst={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteShell>
  );
}

function Block({
  title,
  eyebrow,
  children,
  trailing,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-end justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
              {eyebrow}
            </p>
          )}
          <h2 className="mt-1 font-display text-2xl font-semibold text-[var(--maroon-deep)]">
            {title}
          </h2>
        </div>
        {trailing}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}
function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)]">
      <h3 className="font-display text-base font-semibold text-[var(--maroon-deep)]">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}
function MiniStat({ icon: Icon, value, label }: { icon: any; value: string; label: string }) {
  return (
    <div className="rounded-xl bg-[var(--cream-2)] p-3">
      <div className="flex items-center gap-2 text-[var(--maroon)]">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider text-foreground/70">
          {label}
        </span>
      </div>
      <p className="mt-1 font-display text-lg font-semibold text-[var(--maroon-deep)]">{value}</p>
    </div>
  );
}
function Fact({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--cream-2)] text-[var(--maroon)]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-foreground/70">{label}</p>
        <p className="text-sm font-semibold text-foreground/85 break-words">{value}</p>
      </div>
    </div>
  );
}
function Check({ label, ok }: { label: string; ok: boolean }) {
  return (
    <li className="flex items-center gap-2">
      <CheckCircle2 className={`h-4 w-4 ${ok ? "text-[var(--success)]" : "text-foreground/30"}`} />
      <span className={ok ? "" : "text-foreground/70"}>{label}</span>
    </li>
  );
}
